import React, { useState, useEffect } from 'react';
import {
    Text,
    Modal,
    Services,
    NumberInput
} from '@simplybenefits/sb-ui-library';
import { helpers } from '@simplybenefits/sb-utils';
import { connect } from 'react-redux';
import * as notifier from '../../../actions/notifier.js';
import { setAllotmentAndBalance } from '../../../actions/users.js';

/**
 * SetAllotmentsModal component allows administrators to set spending account allotments and balances
 * for Health Spending Account (HSA) and Lifestyle Spending Account (LSA).
 * Handles validation, rollover calculations, and submission of new values.
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.showModal - Controls modal visibility
 * @param {Function} props.hideModal - Function to close the modal
 * @param {Object} props.coverage - User's coverage information including spending account details
 * @param {Object} props.loader - Loading state object with isLoading boolean
 * @param {Object} props.currentUser - Current user object containing spending account data and metadata
 * @param {Function} props.dispatch - Redux dispatch function for triggering actions
 * @returns {JSX.Element} The rendered SetAllotmentsModal component
 */
const SetAllotmentsModal = ({ 
    showModal, 
    hideModal,
    coverage,
    loader,
    currentUser,
    dispatch
}) => {
    const healthRemaining = currentUser.spending.health.remaining;
    const lifestyleRemaining = currentUser.spending.lifestyle.remaining;

    const [ balances, setBalances ] = useState({ hsaBalance: healthRemaining, lsaBalance: lifestyleRemaining });
    const { lsaBalance, hsaBalance } = balances;

    const [ maximums, setMaximums ] = useState({ hsaMax: currentUser.spending.health.total, lsaMax: currentUser.spending.lifestyle.total });
    const { lsaMax, hsaMax } = maximums;

    const [ errors, setErrors ] = useState({ 
        hsaBalanceError: false, 
        lsaBalanceError: false,
        hsaMaxError: false, 
        lsaMaxError: false
    });
    const { hsaBalanceError, hsaMaxError, lsaBalanceError, lsaMaxError } = errors;

    /**
     * Updates the balance values for HSA or LSA accounts.
     * @param {string} type - The type of balance to update ('hsaBalance' or 'lsaBalance')
     * @param {string|number} balance - The new balance value
     * @returns {void}
     */
    const handleBalanceChange = (type, balance) => setBalances((prevBalances) => ({ ...prevBalances, [type]: balance }));

    /**
     * Updates the maximum values for HSA or LSA accounts.
     * @param {string} type - The type of maximum to update ('hsaMax' or 'lsaMax')
     * @param {string|number} maximum - The new maximum value
     * @returns {void}
     */
    const handleMaximumsChange = (type, maximum) => setMaximums((prevMaximums) => ({ ...prevMaximums, [type]: maximum }));

    /**
     * Validates that account balances do not exceed their respective maximums plus rollover amounts.
     * Displays appropriate error notifications and sets error states if validation fails.
     * @async
     * @function
     * @returns {Promise<boolean>} Returns true if validation fails (balances exceed maximums), false if validation passes
     */
    const isBalanceAboveMaximum = async () => {
        const isLsaBalanceAboveThreshold = Number(lsaBalance) > Number(lsaMax);
        const isHsaBalanceAboveThreshold = Number(hsaBalance) > Number(hsaMax);

        if (isLsaBalanceAboveThreshold && isHsaBalanceAboveThreshold) {
            await dispatch(notifier.displayError({ verbiage: `HSA/LSA balances can't be greater than maximum + rollover.` }));

            setErrors({
                hsaMaxError: true,
                hsaBalanceError: true,
                lsaMaxError: true,
                lsaBalanceError: true
            });

            return true;
        } 

        if (isLsaBalanceAboveThreshold) {
            await dispatch(notifier.displayError({ verbiage: `LSA balance can't be greater than LSA maximum + LSA rollover.` }));

            setErrors((prevErrors) => ({
                ...prevErrors,
                lsaMaxError: true,
                lsaBalanceError: true
            }));

            return true;
        }

        if (isHsaBalanceAboveThreshold) {
            await dispatch(notifier.displayError({ verbiage: `HSA balance can't be greater than HSA maximum + HSA rollover.` }));

            setErrors((prevErrors) => ({
                ...prevErrors,
                hsaMaxError: true,
                hsaBalanceError: true
            }));

            return true;
        }

        return false;
    };

    /**
     * Validates that all required input fields are populated (not empty).
     * Uses the Services.validateInput utility to check for empty values.
     * Updates the error state for any fields that fail validation.
     * @async
     * @function
     * @returns {Promise<boolean>} Returns true if any inputs are empty (validation failed), false if all inputs are populated
     */
    const validateInputs = async () => {
        // Check to see if data is populated
        const hsaMaxValidation = await Services.validateInput('checkEmpty', hsaMax);
        const hsaBalanceValidation = await Services.validateInput('checkEmpty', hsaBalance);
        const lsaMaxValidation = await Services.validateInput('checkEmpty', lsaMax);
        const lsaBalanceValidation = await Services.validateInput('checkEmpty', lsaBalance);

        setErrors({
            hsaMaxError: hsaMaxValidation,
            hsaBalanceError: hsaBalanceValidation,
            lsaMaxError: lsaMaxValidation,
            lsaBalanceError: lsaBalanceValidation
        });

        if (hsaMaxValidation || hsaBalanceValidation || lsaMaxValidation || lsaBalanceValidation) {
            return true;
        }

        return false;
    };

    /**
     * Performs comprehensive validation of all form inputs and business rules.
     * First validates that all inputs are populated, then checks that balances don't exceed maximums.
     * @async
     * @function
     * @returns {Promise<boolean>} Returns true if any validation fails, false if all validation passes
     */
    const validateBalancesAndMaximums = async () => {
        const hasErrors = await validateInputs();

        if (hasErrors) {
            return true;
        }

        // Ensure set balance < set max
        const balanceCheck = await isBalanceAboveMaximum();

        if (balanceCheck) {
            return true;
        }

        return false;
    };

    /**
     * Handles the save operation for allotments and balances.
     * Validates all inputs before proceeding, calculates final values by subtracting rollover amounts,
     * dispatches the save action, and closes the modal on success.
     * @async
     * @function
     * @returns {Promise<void>} No return value - either completes successfully or exits early on validation failure
     */
    const saveBalancesAndMaximums = async () => {
        const hasErrors = await validateBalancesAndMaximums();

        if (hasErrors) {
            return; 
        }

        const healthRollover = currentUser.spending.health.rollover;
        const healthRolloverRemaining = currentUser.spending.health.rolloverRemaining;
        const lifestyleRollover = currentUser.spending.lifestyle.rollover;
        const lifestyleRolloverRemaining = currentUser.spending.lifestyle.rolloverRemaining;

        const data = {
            hsaMax: hsaMax - healthRollover,
            hsaBalance: hsaBalance - healthRolloverRemaining,
            lsaMax: lsaMax - lifestyleRollover,
            lsaBalance: lsaBalance - lifestyleRolloverRemaining
        };

        const success = await dispatch(setAllotmentAndBalance(currentUser.meta.userID, data));
        
        if (success) {
            hideModal();
        }
    };

    useEffect(() => {
        validateInputs();
    }, [ lsaMax, hsaMax, lsaBalance, hsaBalance ]);
    
    return (
        <Modal
            open={showModal}
            onClose={hideModal}
            style={{ overflow: 'auto', 'WebkitOverflowScrolling': 'touch' }}
            medium
            title="Set Allotments & Balances"
            primaryButton={{
                label: "Set Allotments & Balances",
                onClick: saveBalancesAndMaximums
            }}
            loader={loader}
        >
            {
                coverage.spendingAccounts.health?.hasBenefit === 1
                    ? (
                        <div style={{ marginBottom: '20px' }}>
                            <Text medium bold>Health Spending Account</Text>
                            <Text>{`Remaining Rollover: $${ helpers.formatNumber(currentUser.spending.health.rolloverRemaining) }`}</Text>
                            <Text>{`Total Rollover: $${ helpers.formatNumber(currentUser.spending.health.rollover) }`}</Text>
                            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                                <NumberInput
                                    label="Balance (Including Remaining Rollover)"
                                    fullWidth
                                    customStyle={{
                                        marginRight: '10px'
                                    }}
                                    value={ hsaBalance || '' }
                                    variant="outlined"
                                    prefix="$"
                                    onChange={(e) => handleBalanceChange('hsaBalance', e.target.value)}
                                    error={Boolean(hsaBalanceError)}
                                />
                                                
                                <NumberInput
                                    label="Maximum (Including Total Rollover)"
                                    fullWidth
                                    customStyle={{
                                        marginLeft: '10px'
                                    }}
                                    value={ hsaMax || ''}
                                    variant="outlined"
                                    prefix="$"
                                    onChange={(e) => handleMaximumsChange('hsaMax', e.target.value)}
                                    error={Boolean(hsaMaxError)}
                                />
                            </div>
                        </div>
                    )
                    : null
            }

            {
                coverage.spendingAccounts.lifestyle?.hasBenefit === 1
                    ? (
                        <>
                            <Text medium bold>Lifestyle Spending Account</Text>
                            <Text>{`Remaining Rollover: $${ helpers.formatNumber(currentUser.spending.lifestyle.rolloverRemaining) }`}</Text>
                            <Text>{`Total Rollover: $${ helpers.formatNumber(currentUser.spending.lifestyle.rollover) }`}</Text>
                            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                                <NumberInput
                                    label="Balance (Including Remaining Rollover)"
                                    fullWidth
                                    customStyle={{
                                        marginRight: '10px'
                                    }}
                                    value={ lsaBalance || '' }
                                    variant="outlined"
                                    prefix="$"
                                    onChange={(e) => handleBalanceChange('lsaBalance', e.target.value)}
                                    error={Boolean(lsaBalanceError)}
                                />

                                <NumberInput
                                    label="Maximum (Including Total Rollover)"
                                    fullWidth
                                    customStyle={{
                                        marginLeft: '10px'
                                    }}
                                    value={ lsaMax || '' }
                                    variant="outlined"
                                    prefix="$"
                                    onChange={(e) => handleMaximumsChange('lsaMax', e.target.value)}
                                    error={Boolean(lsaMaxError)}
                                />
                            </div>
                        </>
                    )
                    : null
            }
            <Text margin="10px 0 0">
                    No billing adjustments will be applied. The provided balance(s) and maximum(s) will become the member's new values. 
                    Agents should consider rollover amounts when updating values.
            </Text>
        </Modal>
    );
};

/**
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    masterLoading: state.master.loading,
    currentUser: state.users.currentUser,
    coverage: state.users.currentUser.coverage,
    accessType: state.master.access,
    hsaClaimTypes: state.master.claimTypes,
    adjudicators: state.master.adjudicators,
    master: state.master
});

export default (connect(mapStateToProps)(SetAllotmentsModal));